/**
 * Field Permissions Tab
 * 
 * Configure which fields are accessible for the permission.
 * Uses a checkbox tree similar to the reference implementation.
 * 
 * Fields are loaded from the global fields store (hydrated at login).
 */

import { useState, useEffect, useMemo } from 'react';
import { Info, ChevronDown, ChevronRight, Check, Minus } from 'lucide-react';
import type { Permission } from '@/stores/access-control';
import { useFieldsStore } from '@/stores/fields';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface FieldPermissionsTabProps {
    permission: Permission;
    localPermission: Partial<Permission>;
    policyName: string;
    /** Fields that are required by app minimal permissions */
    appMinimalFields?: string[] | null;
    onUpdate: (updates: Partial<Permission>) => void;
}

interface FieldNode {
    field: string;
    name: string;
    type: string;
    children?: FieldNode[];
    isGroup?: boolean;
}

export function FieldPermissionsTab({
    permission,
    localPermission,
    policyName,
    appMinimalFields,
    onUpdate,
}: FieldPermissionsTabProps) {
    // Get fields from global store
    const { getFieldsForCollectionSorted, hydrated, loading, hydrate } = useFieldsStore();

    // Force hydrate fields if not loaded
    useEffect(() => {
        if (!hydrated && !loading) {
            console.log('[FieldPermissionsTab] Fields not hydrated, forcing hydration');
            hydrate(true);
        }
    }, [hydrated, loading, hydrate]);

    // Get fields for this collection
    const fields = useMemo(() => {
        const collectionFields = getFieldsForCollectionSorted(permission.collection);
        console.log('[FieldPermissionsTab] Fields for', permission.collection, ':', collectionFields.length);
        return collectionFields;
    }, [getFieldsForCollectionSorted, permission.collection, hydrated]);

    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
    const [selectedFields, setSelectedFields] = useState<Set<string>>(new Set());

    // Initialize selected fields from permission
    useEffect(() => {
        const currentFields = localPermission.fields;
        if (currentFields === null || currentFields === undefined) {
            // No fields set - means no access
            setSelectedFields(new Set());
        } else if (currentFields.includes('*')) {
            // All fields selected
            const allFieldNames = fields.map(f => f.field);
            setSelectedFields(new Set(allFieldNames));
        } else {
            setSelectedFields(new Set(currentFields));
        }
    }, [localPermission.fields, fields]);

    // Build field tree (group fields under their parents)
    const buildFieldTree = (): FieldNode[] => {
        const nodes: FieldNode[] = [];
        const groupMap = new Map<string, FieldNode>();

        // First pass: create all nodes
        for (const field of fields) {
            const node: FieldNode = {
                field: field.field,
                name: field.name || field.field,
                type: field.type,
                isGroup: field.meta?.special?.includes('group') || field.meta?.special?.includes('alias'),
            };

            if (node.isGroup) {
                node.children = [];
                groupMap.set(field.field, node);
            }

            // Check if this field belongs to a group
            if (field.meta?.group && groupMap.has(field.meta.group)) {
                groupMap.get(field.meta.group)!.children!.push(node);
            } else {
                nodes.push(node);
            }
        }

        return nodes;
    };

    const fieldTree = buildFieldTree();

    // Get all field names recursively
    const getAllFieldNames = (nodes: FieldNode[]): string[] => {
        const names: string[] = [];
        for (const node of nodes) {
            names.push(node.field);
            if (node.children) {
                names.push(...getAllFieldNames(node.children));
            }
        }
        return names;
    };

    const allFieldNames = getAllFieldNames(fieldTree);
    const allSelected = allFieldNames.length > 0 &&
        allFieldNames.every(name => selectedFields.has(name));
    const someSelected = allFieldNames.some(name => selectedFields.has(name));

    const handleToggleField = (fieldName: string, children?: FieldNode[]) => {
        const newSelected = new Set(selectedFields);

        if (newSelected.has(fieldName)) {
            newSelected.delete(fieldName);
            // Also deselect children
            if (children) {
                for (const child of getAllFieldNames(children)) {
                    newSelected.delete(child);
                }
            }
        } else {
            newSelected.add(fieldName);
            // Also select children
            if (children) {
                for (const child of getAllFieldNames(children)) {
                    newSelected.add(child);
                }
            }
        }

        setSelectedFields(newSelected);
        updatePermissionFields(newSelected);
    };

    const handleToggleAll = () => {
        if (allSelected) {
            setSelectedFields(new Set());
            updatePermissionFields(new Set());
        } else {
            const all = new Set(allFieldNames);
            setSelectedFields(all);
            updatePermissionFields(all);
        }
    };

    const updatePermissionFields = (selected: Set<string>) => {
        if (selected.size === 0) {
            onUpdate({ fields: null });
        } else if (selected.size === allFieldNames.length) {
            onUpdate({ fields: ['*'] });
        } else {
            onUpdate({ fields: Array.from(selected) });
        }
    };

    const toggleGroup = (groupField: string) => {
        const newExpanded = new Set(expandedGroups);
        if (newExpanded.has(groupField)) {
            newExpanded.delete(groupField);
        } else {
            newExpanded.add(groupField);
        }
        setExpandedGroups(newExpanded);
    };

    const handleExpandAll = () => {
        const groups = fieldTree.filter(n => n.isGroup).map(n => n.field);
        setExpandedGroups(new Set(groups));
    };

    const handleCollapseAll = () => {
        setExpandedGroups(new Set());
    };

    // Get checkbox state for a field
    const getCheckboxState = (node: FieldNode): 'checked' | 'unchecked' | 'indeterminate' => {
        if (!node.children || node.children.length === 0) {
            return selectedFields.has(node.field) ? 'checked' : 'unchecked';
        }

        const childNames = getAllFieldNames(node.children);
        const selectedCount = childNames.filter(name => selectedFields.has(name)).length;

        if (selectedCount === 0) return 'unchecked';
        if (selectedCount === childNames.length) return 'checked';
        return 'indeterminate';
    };

    const hasGroups = fieldTree.some(n => n.isGroup);

    if (loading || !hydrated) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Notice */}
            <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/20">
                <Info className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                <div className="text-sm ">
                    <p>
                        Select which fields the <strong>{policyName || 'Public'}</strong> policy
                        can <strong>{permission.action}</strong> in the{' '}
                        <strong>{permission.collection}</strong> collection.
                    </p>
                </div>
            </div>

            {/* Header with expand/collapse */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-foreground">Fields</span>
                    <span className="text-sm text-muted-foreground">
                        {selectedFields.size} of {allFieldNames.length} selected
                    </span>
                </div>
                {hasGroups && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <span>Expand</span>
                        <Button
                            type="button"
                            variant="link"
                            onClick={handleExpandAll}
                            className="h-auto p-0 text-primary"
                        >
                            All
                        </Button>
                        <span>/</span>
                        <Button
                            type="button"
                            variant="link"
                            onClick={handleCollapseAll}
                            className="h-auto p-0 text-primary"
                        >
                            None
                        </Button>
                    </div>
                )}
            </div>

            {/* Select All */}
            <div className="rounded-lg border border-border bg-background">
                <Button
                    type="button"
                    variant="ghost"
                    onClick={handleToggleAll}
                    className="flex w-full items-center justify-start gap-3 p-3 text-left h-auto"
                >
                    <div className={`flex h-5 w-5 items-center justify-center rounded border ${allSelected
                        ? 'border-primary bg-primary'
                        : someSelected
                            ? 'border-primary bg-primary'
                            : 'border-border'
                        }`}>
                        {allSelected && <Check className="h-3 w-3 text-white" />}
                        {!allSelected && someSelected && <Minus className="h-3 w-3 text-white" />}
                    </div>
                    <span className="font-medium text-foreground">
                        {allSelected ? 'Deselect All' : 'Select All Fields'}
                    </span>
                </Button>
            </div>

            {/* Field Tree */}
            <div className="rounded-lg border border-border bg-background">
                {fieldTree.length === 0 ? (
                    <div className="p-6 text-center text-sm text-muted-foreground">
                        No fields found for this collection.
                    </div>
                ) : (
                    <div className="divide-y divide-border">
                        {fieldTree.map((node) => (
                            <FieldNode
                                key={node.field}
                                node={node}
                                depth={0}
                                selectedFields={selectedFields}
                                expandedGroups={expandedGroups}
                                onToggle={handleToggleField}
                                onToggleGroup={toggleGroup}
                                getCheckboxState={getCheckboxState}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* App Minimal Permission Warning */}
            {appMinimalFields && appMinimalFields.length > 0 && (
                <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4  dark:border-amber-800 dark:bg-amber-900/20">
                    <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                    <div className="text-sm">
                        <p>
                            The following minimum permissions are automatically applied when
                            "App Access" is enabled. You can grant additional permissions if
                            needed, but you cannot reduce permissions below this level.
                        </p>
                    </div>
                </div>
            )}

            {/* Raw Permission Value (JSON) */}
            <div className="rounded-lg border border-border bg-muted p-4">
                <pre className="text-sm text-foreground overflow-auto">
                    {JSON.stringify(localPermission.fields, null, 2)}
                </pre>
            </div>
        </div>
    );
}

// Field Node Component
interface FieldNodeProps {
    node: FieldNode;
    depth: number;
    selectedFields: Set<string>;
    expandedGroups: Set<string>;
    onToggle: (field: string, children?: FieldNode[]) => void;
    onToggleGroup: (field: string) => void;
    getCheckboxState: (node: FieldNode) => 'checked' | 'unchecked' | 'indeterminate';
}

function FieldNode({
    node,
    depth,
    selectedFields,
    expandedGroups,
    onToggle,
    onToggleGroup,
    getCheckboxState,
}: FieldNodeProps) {
    const isExpanded = expandedGroups.has(node.field);
    const checkboxState = getCheckboxState(node);
    const hasChildren = node.children && node.children.length > 0;

    return (
        <div>
            <div
                className="flex items-center gap-2 p-3 hover:bg-accent"
                style={{ paddingLeft: `${12 + depth * 24}px` }}
            >
                {/* Expand/Collapse for groups */}
                {hasChildren ? (
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => onToggleGroup(node.field)}
                        className="h-6 w-6"
                    >
                        {isExpanded ? (
                            <ChevronDown className="h-4 w-4" />
                        ) : (
                            <ChevronRight className="h-4 w-4" />
                        )}
                    </Button>
                ) : (
                    <div className="w-5" />
                )}

                {/* Checkbox */}
                <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => onToggle(node.field, node.children)}
                    className={`flex h-5 w-5 items-center justify-center rounded border ${checkboxState === 'checked'
                        ? 'border-primary bg-primary'
                        : checkboxState === 'indeterminate'
                            ? 'border-primary bg-primary'
                            : 'border-border hover:border-primary'
                        }`}
                >
                    {checkboxState === 'checked' && <Check className="h-3 w-3 text-white" />}
                    {checkboxState === 'indeterminate' && <Minus className="h-3 w-3 text-white" />}
                </Button>

                {/* Field name */}
                <span className="flex-1 font-mono text-sm text-foreground">
                    {node.field}
                </span>

                {/* Field type */}
                <span className="text-xs text-muted-foreground">
                    {node.type}
                </span>
            </div>

            {/* Children */}
            {hasChildren && isExpanded && (
                <div className="border-t border-border bg-muted/50">
                    {node.children!.map((child) => (
                        <FieldNode
                            key={child.field}
                            node={child}
                            depth={depth + 1}
                            selectedFields={selectedFields}
                            expandedGroups={expandedGroups}
                            onToggle={onToggle}
                            onToggleGroup={onToggleGroup}
                            getCheckboxState={getCheckboxState}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
